/**
 * Best-effort scrubbing of personal data before anything is sent to Sentry.
 *
 * This is a safety net, not a guarantee: the rule stays "never put personal
 * or health data in errors, logs or Sentry context".
 */

const FILTERED = "[Filtered]";

const SENSITIVE_KEY =
  /^(e-?mail|username|phone|phonenumber|mobile|telecom|firstname|lastname|given|family|birthdate|dateofbirth|nir|ssn|address|postalcode|zip|password|otp|userinputcode|token|accesstoken|refreshtoken|authorization|cookie|set-cookie|content|valuestring|transcription|summary|ip_address)$/i;

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// French NIR: 13 digits + optional 2-digit key, optional spaces, Corsica 2A/2B.
const NIR =
  /\b[12]\s?\d{2}\s?(?:0[1-9]|1[0-2]|[2-9]\d)\s?(?:\d{2}|2A|2B)\s?\d{3}\s?\d{3}(?:\s?\d{2})?\b/gi;
// International (+XX...) or French national (0X XX XX XX XX) phone numbers.
const PHONE = /(?:\+\d{1,3}[\s.-]?|\b)(?:0[\s.-]?)?[1-9](?:[\s.-]?\d{2}){4}\b/g;

const MAX_DEPTH = 8;

export const scrubString = (value: string): string =>
  value
    .replace(EMAIL, "[email]")
    .replace(NIR, "[nir]")
    .replace(PHONE, "[phone]");

export const scrubValue = (value: unknown, depth = 0): unknown => {
  if (typeof value === "string") {
    return scrubString(value);
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  if (depth >= MAX_DEPTH) {
    return FILTERED;
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }

  const result: Record<string, unknown> = {};

  for (const [key, item] of Object.entries(value)) {
    result[key] = SENSITIVE_KEY.test(key)
      ? FILTERED
      : scrubValue(item, depth + 1);
  }

  return result;
};

// Query strings carry search terms, phone numbers and e-mails (often
// URL-encoded, which the patterns above would miss): they are dropped.
const QUERY_KEY = /^(http\.query|url\.query|http\.fragment|url\.fragment)$/;
const URL_KEY = /^(url|http\.url|url\.full|http\.target)$/;

const decode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** A URL without its query string or fragment, the rest scrubbed. */
export const scrubUrl = (url: string): string =>
  scrubString(decode(url.split(/[?#]/)[0]));

/** Free text that may contain URLs, such as a span description. */
const scrubText = (text: string): string =>
  scrubString(decode(text.replace(/[?#][^\s]*/g, "")));

const scrubSpanData = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => !QUERY_KEY.test(key))
      .map(([key, value]) => [
        key,
        typeof value === "string" && URL_KEY.test(key)
          ? scrubUrl(value)
          : scrubValue(value),
      ])
  );

type ScrubbableSpan = {
  description?: string;
  data?: Record<string, unknown>;
};

const scrubSpan = <T extends ScrubbableSpan>(span: T): T => {
  if (typeof span.description === "string") {
    span.description = scrubText(span.description);
  }

  if (span.data) {
    span.data = scrubSpanData(span.data);
  }

  return span;
};

type ScrubbableEvent = {
  message?: string;
  user?: { id?: string | number } & Record<string, unknown>;
  request?: Record<string, unknown>;
  exception?: { values?: Array<{ value?: string } & Record<string, unknown>> };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  spans?: ScrubbableSpan[];
  breadcrumbs?: ScrubbableBreadcrumb[];
};

type ScrubbableBreadcrumb = {
  message?: string;
  data?: Record<string, unknown>;
};

type ScrubbableLog = {
  message: unknown;
  attributes?: Record<string, unknown>;
};

export const scrubBreadcrumb = <T extends ScrubbableBreadcrumb>(
  breadcrumb: T
): T => {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubString(breadcrumb.message);
  }

  if (breadcrumb.data) {
    breadcrumb.data = scrubSpanData(breadcrumb.data);
  }

  return breadcrumb;
};

// Typed loosely on purpose: works with both @sentry/nestjs and @sentry/react events.
export const scrubEvent = <T extends object>(input: T): T => {
  const event = input as ScrubbableEvent;

  // Only the technical user id is allowed to reach Sentry.
  event.user = event.user?.id !== undefined ? { id: event.user.id } : undefined;

  if (event.request) {
    const { url, method } = event.request;

    event.request = {
      ...(typeof url === "string" ? { url: scrubUrl(url) } : {}),
      ...(method ? { method } : {}),
    };
  }

  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }

  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === "string") {
      exception.value = scrubString(exception.value);
    }
  });

  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>;
  }

  if (event.contexts) {
    const { trace, ...contexts } = event.contexts as Record<string, any>;

    event.contexts = {
      ...(scrubValue(contexts) as Record<string, unknown>),
      ...(trace ? { trace: scrubSpan(trace) } : {}),
    };
  }

  if (event.spans) {
    event.spans = event.spans.map(scrubSpan);
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }

  return input;
};

export const scrubLog = <T extends ScrubbableLog>(log: T): T => {
  log.message = scrubString(String(log.message));

  if (log.attributes) {
    log.attributes = scrubValue(log.attributes) as Record<string, unknown>;
  }

  return log;
};
